import { useEffect, useState } from 'react';
import { Box, Tab, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import useUserAuth from '../../../utils/useUserAuth';

import { centersDetail } from '../../../api/store-management';
import AnimationPage from '../../../pages/AnimationPage';
import StoreTab1 from '../StoreTab1';
import StoreTab2 from '../StoreTab2';
import StoreTab3 from '../StoreTab3';
import StoreTabBefore3 from '../StoreTabBefore3';
import StoreTab7 from '../StoreTab7';
import LockerManagement from '../lockerManagment/LockerManagement';
import ProductManagement from '../productManagement/ProductManagement';
import PermissionSettings from '../permissionSettings/PermissionSettings';
import ElectronicContract from '../electronicContract/ElectronicContract';
import { PageContainer, TabsHeader, StyledTabs, PageDivider } from './StoreDetailsStyled';

// 탭 정의 — value 는 기존 라우팅/링크와 호환을 위해 그대로 유지한다.
const BASE_TAB_DEFS = [
  { value: 2, labelKey: 'Studio Information' },
  { value: 1, labelKey: '사업자 정보' },
  { value: 3, labelKey: 'Center Terms of Use' },
  { value: 4, labelKey: 'Studio Policies' },
];
const CONTRACT_TAB = { value: 9, labelKey: 'Electronic contract management' };
// 골프 관리(타석 배치·회원앱 예약설정·키오스크 설정) — SUPERADMIN 항상, 매니저는 기업회원 골프 관리 설정이 켜진 경우만
const GOLF_TAB = { value: 7, labelKey: 'Golf Management' };

const DEFAULT_TAB = 2;

const StoreDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useSelector(state => state.user.user);
  const token = user?.token;
  const canContractManage = useUserAuth('CENTER_CONTRACT_MANAGE');
  const canSeeGolf = user?.userRole === 'ROLE_SUPERADMIN' || (user?.userRole === 'ROLE_MANAGER' && user?.golfYn === 'Y');

  // URL ↔ 선택된 탭 동기화 (뒤로가기/앞으로가기에서도 일치)
  const requestedTab = Number(searchParams.get('tab')) || DEFAULT_TAB;
  // 골프 탭(7)은 노출 조건이 있어 URL 로 직접 들어와도 못 보는 계정이면 기본 탭으로 돌린다
  const initialTab = requestedTab === GOLF_TAB.value && !canSeeGolf ? DEFAULT_TAB : requestedTab;
  const [selectedTab, setSelectedTab] = useState(initialTab);

  useEffect(() => {
    const next = Number(searchParams.get('tab'));
    if (next === GOLF_TAB.value && !canSeeGolf) return;
    if (next && next !== selectedTab) setSelectedTab(next);
    // selectedTab 은 의존성에서 제외 — URL 변경(뒤로가기 등)에만 반응한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleChange = (_event, newValue) => {
    if (newValue === 9 && !canContractManage) return;
    setSelectedTab(newValue);
    setSearchParams({ tab: newValue });
  };

  // 센터 상세 조회 (React Query)
  const { data: centerData, refetch } = useQuery({
    queryKey: ['centerDetail', id, token],
    queryFn: () => centersDetail(token, id),
    enabled: !!token && !!id,
  });

  const branchInfo = centerData?.centerInfo;
  const centerDetailInfo = centerData?.managerInfo;
  const branchDetailInfo = centerData?.branchDetailInfo;
  const centerId = centerData?.centerInfo?.id;
  // 하위 지점(BRANCH) 로그인 시 user.branch.branchId 사용, 그 외에는 API 응답 사용
  const branchId = user?.centerType === 'BRANCH' ? user?.branch?.branchId : centerData?.branchDetailInfo?.branchId;

  const tabDefs = [...BASE_TAB_DEFS, ...(canSeeGolf ? [GOLF_TAB] : []), CONTRACT_TAB];

  // roomListInfo 는 자식(StoreTab3)이 setter 로 직접 갱신해야 해서 별도 state 로 둔다.
  // 응답이 도착할 때마다 초기값을 동기화한다.
  const [roomListInfo, setRoomListInfo] = useState();
  useEffect(() => {
    setRoomListInfo(centerData?.branchExerciseRoomInfoList);
  }, [centerData]);

  // StoreTab1 의 신규 등록 버튼을 외부에서 트리거하기 위한 헬퍼 (기존 동작 유지)
  const triggerStoreCreateButton = () => {
    document.getElementById('store-create-button')?.click();
  };

  const renderTab = () => {
    switch (selectedTab) {
      case 1:
        return (
          <StoreTab1
            setSelectedTab={setSelectedTab}
            createStore={triggerStoreCreateButton}
            centerDetailInfo={centerDetailInfo}
            branchInfo={branchInfo}
            centerId={centerId}
          />
        );
      case 2:
        return (
          <StoreTab2
            setSelectedTab={setSelectedTab}
            branchDetailInfo={branchDetailInfo}
            branchId={branchId}
            centerDetailInfo={branchInfo}
            managerInfo={centerDetailInfo}
          />
        );
      case 3:
        return (
          <StoreTabBefore3 setSelectedTab={setSelectedTab} branchDetailInfo={branchDetailInfo} branchId={branchId} />
        );
      case 4:
        return (
          <StoreTab3
            roomListInfo={roomListInfo}
            setRoomListInfo={setRoomListInfo}
            branchId={branchId}
            getDetailCenter={refetch}
            branchDetailInfo={branchDetailInfo}
            setSelectedTab={setSelectedTab}
          />
        );
      // 아래 case 5/6 은 현재 상단 Tab 자체가 비활성화돼 있어 도달 불가능하다.
      // 향후 Tab 을 다시 노출할 때 그대로 살릴 수 있도록 분기는 보존한다.
      case 5:
        return <ProductManagement branchId={branchId} />;
      case 6:
        return (
          <LockerManagement
            setSelectedTab={setSelectedTab}
            branchDetailInfo={branchDetailInfo}
            branchId={branchId}
            centerId={centerId}
          />
        );
      case 7:
        if (!canSeeGolf) return null;
        return (
          <StoreTab7
            setSelectedTab={setSelectedTab}
            branchDetailInfo={branchDetailInfo}
            branchId={branchId}
            centerId={centerId}
          />
        );
      case 8:
        return <PermissionSettings />;
      case 9:
        return <ElectronicContract branchId={branchId} branchDetailInfo={branchDetailInfo} />;

      default:
        return null;
    }
  };

  return (
    <AnimationPage>
      <div>
        <PageContainer elevation={0}>
          <TabsHeader>
            <Box>
              <StyledTabs
                value={selectedTab}
                onChange={handleChange}
                textColor="secondary"
                aria-label="store detail tabs">
                {tabDefs.map(tab => {
                  const isContractTab = tab.value === 9;
                  const hasAuth = !isContractTab || canContractManage;
                  const tabElement = (
                    <Tab
                      key={tab.value}
                      value={tab.value}
                      label={t(tab.labelKey)}
                      sx={!hasAuth ? { color: '#ccc !important', opacity: '1 !important' } : {}}
                    />
                  );
                  if (!hasAuth) {
                    return (
                      <Tooltip key={tab.value} title="권한이 없습니다" arrow>
                        {tabElement}
                      </Tooltip>
                    );
                  }
                  return tabElement;
                })}
                {/* 향후 활성화 가능: */}
                {/* <Tab value={5} label={t('Product Management')} /> */}
                {/* <Tab value={6} label={t('Locker Management')} /> */}
                {/* <Tab value={8} label={t('Permission')} /> */}
              </StyledTabs>
            </Box>
          </TabsHeader>

          <PageDivider />
          {renderTab()}
        </PageContainer>
      </div>
    </AnimationPage>
  );
};

export default StoreDetails;
